import { Enum } from "helpers";
import { AutomationType, SurveyCustomFieldType, TriggerListClient } from "helpers/enum";
import { TriggerList } from "helpers/enum";

export const getProviderUnsubscribedLabel = (atsProvider) => {
    // AtsProvider: 1 = JobAdder, 2 = Bullhorn
    return atsProvider === 2 || atsProvider === "Bullhorn" ? "Bullhorn Unsubscribed" : "JobAdder Unsubscribed";
};

export const resolveProviderLabels = (items, atsProvider) => {
    if (!Array.isArray(items)) return items;
    const label = getProviderUnsubscribedLabel(atsProvider);
    return items.map((item) => {
        if (!item?.isProviderLabel) return item;
        const clone = { ...item };
        if ("triggerName" in clone) clone.triggerName = label;
        if ("ifElseFirstColumnName" in clone) clone.ifElseFirstColumnName = label;
        if ("name" in clone) clone.name = label;
        return clone;
    });
};

// The Interview Date trigger's interview type picker. A row picked by hand carries its own list, but
// one compiled by the campaign assistant carries only the chosen interviews, so the box opened empty
// (ROI-412). Falls back to the company's interview types.
export const getInterviewTypeOptions = (thirdColumn, interviewTypes) =>
    thirdColumn?.interviewTypeOptions?.length ? thirdColumn.interviewTypeOptions : (interviewTypes || []);

const CANDIDATE_CUSTOM_FIELD_TRIGGER_IDS = {
    default: { list: TriggerList.CandidateCustomFieldList, multiSelect: TriggerList.CandidateCustomFieldMultiSelectList, date: TriggerList.CandidateCustomFieldDate },
    placement: { list: TriggerList.PlacementCustomFieldList, multiSelect: TriggerList.PlacementCustomFieldMultiSelectList, date: TriggerList.PlacementCustomFieldDate },
    job: { list: TriggerList.JobCustomFieldList, multiSelect: TriggerList.JobCustomFieldMultiSelectList, date: TriggerList.JobCustomFieldDate },
    application: { list: TriggerList.ApplicationCustomFieldList, multiSelect: TriggerList.ApplicationCustomFieldMultiSelectList, date: TriggerList.ApplicationCustomFieldDate },
};

const CLIENT_CUSTOM_FIELD_TRIGGER_IDS = {
    default: { list: TriggerListClient.ClientCustomFieldList, multiSelect: TriggerListClient.ClientCustomFieldMultiSelectList, date: TriggerListClient.ClientCustomFieldDate },
    placement: { list: TriggerListClient.ClientPlacementCustomFieldList, multiSelect: TriggerListClient.ClientPlacementCustomFieldMultiSelectList, date: TriggerListClient.ClientPlacementCustomFieldDate },
    job: { list: TriggerListClient.ClientJobCustomFieldList, multiSelect: TriggerListClient.ClientJobCustomFieldMultiSelectList, date: TriggerListClient.ClientJobCustomFieldDate },
};

// entityType: 'default' (candidate/contact custom fields) | 'placement' | 'job' | 'application' (candidate-side only).
export const mapCustomFieldsToAutomationListOptions = (customFields, automationType, entityType = 'default') => {
    const ids = (automationType === AutomationType.Client ? CLIENT_CUSTOM_FIELD_TRIGGER_IDS : CANDIDATE_CUSTOM_FIELD_TRIGGER_IDS)[entityType];

    return customFields?.slice().filter((item) => {
        const type = item.type?.toLowerCase();
        // GetPlacementCustomFieldByCompany.cs (and the Job/Application equivalents) also return Text and Url
        // custom fields, not just List/Date — those always come back with Values "[]", so they must stay
        // excluded here rather than being accepted as list items with no selectable options.
        if (entityType !== 'default') {
            return type === 'list' || (type === 'date' && !!ids.date);
        }
        return type !== 'date' || ids.date;
    }).map((item) => {
        const isDateType = item.type?.toLowerCase() === "date";

        if (isDateType) {
            const triggerId = ids.date;

            let newCustomFieldItem = JSON.parse(JSON.stringify(Enum.AutomationTriggerCustomDateField));
            newCustomFieldItem.triggerId = triggerId;
            newCustomFieldItem.customFieldTriggerId = `${triggerId}-${item.fieldId}`;
            newCustomFieldItem.triggerName = item.name;
            newCustomFieldItem.thirdColumn.fieldId = item.fieldId;
            return newCustomFieldItem;
        }

        const triggerId = item.isMultiSelect ? ids.multiSelect : ids.list;

        // The item.type === List fallback-to-item.id below is a legacy quirk of the candidate/contact
        // custom fields endpoint, which mixes true custom fields with native ATS fields distinguished by
        // "type". Placement/job custom fields come from their own dedicated endpoint with no such mixing,
        // so every item is a genuine list/multiSelect custom field and must always use the computed triggerId.
        const isDefaultEntity = entityType === 'default';

        let newCustomFieldItem = { ...Enum.AutomationTriggerCustomList };
        newCustomFieldItem.triggerId = (isDefaultEntity && item.type !== SurveyCustomFieldType.List) ? item.id : triggerId;
        newCustomFieldItem.customFieldTriggerId = `${newCustomFieldItem.triggerId}-${item.fieldId}`;
        newCustomFieldItem.triggerName = item.name;
        let options = [...JSON.parse(item.values)];

        if (options.length > 0) {//modify option for candidate/contact custom fields #8372
            options.unshift('Any');
        }
        options = options.map(v => ({
            label: v,
            value: v,
            isDisabled: false
        }));
        newCustomFieldItem.thirdColumn.options = [...options];
        newCustomFieldItem.thirdColumn.isMultiSelect = item.isMultiSelect;
        newCustomFieldItem.thirdColumn.fieldId = item.fieldId;
        return JSON.parse(JSON.stringify(newCustomFieldItem));
    }) || [];
}
